/**
 * 水印工具函数
 * 包含Canvas绘制、容器管理等核心功能
 */

// 默认配置
export const DEFAULT_OPTIONS = {
  content: '', // 水印内容
  fontSize: 14, // 字体大小
  fontFamily: 'sans-serif', // 字体族
  color: 'rgba(0,0,0,0.15)', // 字体颜色
  opacity: 0.15, // 透明度
  rotate: -20, // 旋转角度
  gapX: 200, // 水平间距
  gapY: 150, // 垂直间距
  offsetX: 50, // 水平偏移
  offsetY: 50, // 垂直偏移
  lineHeight: 22, // 行高
  zIndex: 2147483647 // 层级
}

/**
 * 检查是否为数组
 * @param {*} val 要检查的值
 * @returns {Boolean} 是否为数组
 */
function isArray(val) {
  return Object.prototype.toString.call(val) === '[object Array]'
}

/**
 * 创建Canvas数据URL
 * @param {Object} options 水印配置选项
 * @returns {String} Canvas数据URL
 */
export function createCanvasDataURL(options) {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    console.warn('Canvas context not supported')
    return ''
  }

  try {
    // 处理水印内容
    const lines = isArray(opts.content)
      ? opts.content
      : String(opts.content || '').split('\n')

    // 计算文本尺寸
    const maxLineLen = lines.reduce((max, line) =>
      Math.max(max, (line || '').length), 0)
    const textWidth = Math.max(100, maxLineLen * opts.fontSize * 0.6)
    const unitWidth = Math.max(opts.gapX, textWidth + 40)
    const unitHeight = Math.max(opts.gapY, lines.length * opts.lineHeight + 40)

    // 设置Canvas尺寸
    canvas.width = unitWidth
    canvas.height = unitHeight

    // 清除画布
    ctx.clearRect(0, 0, unitWidth, unitHeight)

    // 设置绘制样式
    ctx.globalAlpha = Math.min(1, Math.max(0, opts.opacity))
    ctx.fillStyle = opts.color
    ctx.textAlign = 'left'
    ctx.textBaseline = 'top'
    ctx.font = `${opts.fontSize}px ${opts.fontFamily}`

    // 应用变换
    ctx.save()
    ctx.translate(opts.offsetX, opts.offsetY)
    ctx.rotate((opts.rotate * Math.PI) / 180)

    // 绘制文本
    let y = 0
    lines.forEach((line) => {
      const text = line == null ? '' : String(line)
      ctx.fillText(text, 0, y)
      y += opts.lineHeight
    })

    ctx.restore()

    return canvas.toDataURL()
  } catch (error) {
    console.error('Canvas绘制失败:', error)
    return ''
  }
}

/**
 * 确保水印容器存在
 * @param {HTMLElement} target 目标元素
 * @param {String} id 容器ID
 * @param {Number} zIndex 层级
 * @returns {HTMLElement} 水印容器元素
 */
export function ensureContainer(target, id, zIndex) {
  const root = target || document.body
  let container = root.querySelector(`#${id}`)

  if (container) {
    return container
  }

  // 创建新的水印容器
  container = document.createElement('div')
  container.id = id
  container.style.cssText = `
    pointer-events: none;
    position: ${root === document.body ? 'fixed' : 'absolute'};
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    z-index: ${zIndex || DEFAULT_OPTIONS.zIndex};
    background-repeat: repeat;
    background-position: 0 0;
  `

  // 添加到DOM
  if (root === document.body) {
    document.body.appendChild(container)
  } else {
    // 确保父元素有相对定位
    const computedStyle = getComputedStyle(root)
    if (computedStyle.position === 'static') {
      root.style.position = 'relative'
    }
    root.appendChild(container)
  }

  return container
}

/**
 * 在指定元素上应用水印
 * @param {HTMLElement} el 目标元素
 * @param {Object} options 水印选项
 * @returns {Function} 清除水印的函数
 */
export function applyElementWatermark(el, options = {}) {
  if (!el || !el.nodeType) {
    console.warn('Invalid element provided for watermark')
    return () => {}
  }

  const opts = { ...DEFAULT_OPTIONS, ...options }
  const containerId = opts.containerId || `company-watermark-${Date.now()}`

  try {
    const url = createCanvasDataURL(opts)
    const container = ensureContainer(el, containerId, opts.zIndex)
    container.style.backgroundImage = `url('${url}')`

    // 返回清除函数
    return () => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container)
      }
    }
  } catch (error) {
    console.error('元素水印应用失败:', error)
    return () => {}
  }
}

/**
 * 验证水印选项
 * @param {Object} options 要验证的选项
 * @returns {Object} 验证后的选项
 */
export function validateOptions(options = {}) {
  const validated = { ...options }

  // 验证数值类型
  const numberFields = ['fontSize', 'opacity', 'rotate', 'gapX', 'gapY', 'offsetX', 'offsetY', 'lineHeight', 'zIndex']
  numberFields.forEach(field => {
    if (typeof validated[field] === 'string') {
      const num = parseFloat(validated[field])
      validated[field] = isNaN(num) ? DEFAULT_OPTIONS[field] : num
    }
  })

  // 验证颜色
  if (validated.color && typeof validated.color !== 'string') {
    validated.color = DEFAULT_OPTIONS.color
  }

  // 验证字体族
  if (validated.fontFamily && typeof validated.fontFamily !== 'string') {
    validated.fontFamily = DEFAULT_OPTIONS.fontFamily
  }

  return validated
}
